
import React, { useEffect } from "react";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { Send } from "lucide-react";
import CreateNotificationForm from "./CreateNotificationForm";
import { useAuth } from "@/context/AuthContext";
import { useCreateNotification } from "@/hooks/useCreateNotification";
import { useIsMobile } from "@/hooks/use-mobile";

interface NotificationsCreateTabProps {
  onCancel: () => void;
}

const NotificationsCreateTab = ({
  onCancel
}: NotificationsCreateTabProps) => {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  
  useEffect(() => {
    if (isMobile) {
      document.body.classList.add('has-mobile-notification');
      document.body.classList.add('form-fixed');
    }
    
    return () => {
      document.body.classList.remove('form-fixed');
      // Keep the has-mobile-notification class as it might be needed by parent components
    };
  }, [isMobile]);
  
  const handleSuccess = () => {
    // Wait a brief moment before switching tabs to ensure data is refreshed
    setTimeout(() => {
      onCancel();
    }, isMobile ? 300 : 200); // Slightly longer delay on mobile for better UX
  };
  
  const {
    newNotification,
    isAnalyzing,
    setIsAnalyzing,
    isPending,
    handleInputChange,
    handleSelectChange,
    handleCheckboxChange,
    handleInternalCheckboxChange,
    toggleExternalRecipient,
    handleProductsChange,
    handleAIAnalysisComplete,
    handleSubmit
  } = useCreateNotification(user?.id, user?.name, handleSuccess);

  return (
    <Card 
      className={isMobile ? "fixed-mobile-card" : ""} 
      style={isMobile ? { 
        position: 'static', 
        transform: 'none', 
        touchAction: 'auto',
        pointerEvents: 'auto'
      } : undefined}
    >
      <CardHeader>
        <CardTitle className="flex items-center">
          <Send className="mr-2 h-5 w-5" />
          Nieuwe melding aanmaken
        </CardTitle>
      </CardHeader>
      <CardContent>
        <CreateNotificationForm
          notification={newNotification}
          isPending={isPending}
          isAnalyzing={isAnalyzing}
          onInputChange={handleInputChange}
          onSelectChange={handleSelectChange}
          onCheckboxChange={handleCheckboxChange}
          onInternalCheckboxChange={handleInternalCheckboxChange}
          onToggleExternalRecipient={toggleExternalRecipient}
          onProductsChange={handleProductsChange}
          onAIAnalysisComplete={handleAIAnalysisComplete}
          setIsAnalyzing={setIsAnalyzing}
          onCancel={onCancel}
          onSubmit={handleSubmit}
        />
      </CardContent>
    </Card>
  );
};

export default NotificationsCreateTab;
